import { useCallback, useEffect, useState } from "react";
import {
  getCoverageHealth,
  getCoveragePatient,
  getCoverageReadiness,
  resolveCoverageReview,
  verifyCoveragePatient,
} from "@/services/coverageApi";
import { mapPatientWorkspace, mapReadinessItems } from "@/services/coverageMappers";

export function useCoverageIntelligence() {
  const [health, setHealth] = useState(null);
  const [patients, setPatients] = useState([]);
  const [source, setSource] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [queueError, setQueueError] = useState(null);
  const [queueLoading, setQueueLoading] = useState(true);

  const [activePatient, setActivePatient] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyingPatientId, setVerifyingPatientId] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const loadQueue = useCallback(async () => {
    setQueueLoading(true);
    setQueueError(null);
    setForbidden(false);
    try {
      const [healthRes, readinessRes] = await Promise.all([
        getCoverageHealth().catch(() => null),
        getCoverageReadiness(),
      ]);
      setHealth(healthRes);
      setSource(readinessRes?.source || null);
      setTenant(readinessRes?.tenant || healthRes?.tenant || null);
      setPatients(mapReadinessItems(readinessRes));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setForbidden(true);
        setQueueError(err.response?.data?.detail || "This organization is not mapped to Coverage Intelligence.");
        setPatients([]);
      } else {
        setQueueError(err.response?.data?.detail?.message || err.message || "Failed to load coverage readiness.");
      }
    } finally {
      setQueueLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const openPatient = useCallback(async (patient) => {
    setActivePatient(patient);
    setWorkspaceLoading(true);
    try {
      const res = await getCoveragePatient(patient.patient_id);
      const view = mapPatientWorkspace(res);
      setWorkspace(view);
      setActivePatient({ ...patient, ...view, patient_id: patient.patient_id });
    } catch (err) {
      setWorkspace(null);
      throw err;
    } finally {
      setWorkspaceLoading(false);
    }
  }, []);

  const closePatient = useCallback(() => {
    setActivePatient(null);
    setWorkspace(null);
  }, []);

  const verifyPatient = useCallback(async (patient) => {
    const target = patient || activePatient;
    if (!target?.patient_id) return;
    setVerifyLoading(true);
    setVerifyingPatientId(target.patient_id);
    try {
      const res = await verifyCoveragePatient(target.patient_id, target.appointment_id);
      const view = mapPatientWorkspace(res);
      setWorkspace(view);
      setActivePatient((prev) => ({ ...(prev || target), ...view, patient_id: target.patient_id }));
      await loadQueue();
      return view;
    } finally {
      setVerifyLoading(false);
      setVerifyingPatientId(null);
    }
  }, [activePatient, loadQueue]);

  const resolveReview = useCallback(async (reviewId, body) => {
    const res = await resolveCoverageReview(reviewId, body);
    if (activePatient?.patient_id) {
      await openPatient(activePatient);
    }
    await loadQueue();
    return res;
  }, [activePatient, loadQueue, openPatient]);

  return {
    health,
    patients,
    source,
    tenant,
    queueError,
    queueLoading,
    forbidden,
    activePatient,
    workspace,
    workspaceLoading,
    verifyLoading,
    verifyingPatientId,
    loadQueue,
    openPatient,
    closePatient,
    verifyPatient,
    resolveReview,
  };
}
